import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { CertificationRosterEntry, ExamAttemptSummary } from '../core/models';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** VAR-agent certification admin page (AB#1827) — grants, renews and revokes
 * the certification a `var` principal needs to act on cases. Mirrors
 * VarPrincipalReviewComponent's structure (standalone, signal-backed fields
 * so a direct component-instance write from a test notifies the template's
 * `@if`/`[(ngModel)]` consumers synchronously between two detectChanges()
 * calls, load-on-init, inline confirm-before-destructive-action). */
@Component({
  selector: 'app-agent-certification',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltip],
  template: `
    <div class="bt-page">
      <div class="bt-page__head">
        <h1 class="bt-page__title">Agent Certification</h1>
        <p class="bt-page__subtitle">
          VAR-agent certification gate — grant, renew, or revoke a partner agent's ability to act on cases.
        </p>
      </div>

      <div class="bt-panel cert-panel">
        @if (loading) {
          <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading certifications…</span></div>
        } @else if (!roster.length) {
          <div class="bt-empty" data-testid="cert-empty">
            <span class="bt-empty__icon">🎓</span>
            <span class="bt-empty__title">No VAR agents</span>
            <span class="bt-empty__hint">No VAR-partner agents are provisioned yet.</span>
          </div>
        } @else {
          <table class="bt-table" role="table" aria-label="VAR agent certifications" data-testid="cert-table">
            <thead>
              <tr>
                <th ngbTooltip="The agent's sign-in email">Agent</th>
                <th ngbTooltip="Whether this agent is currently certified to act on cases">Status</th>
                <th ngbTooltip="When the current certification expires">Expires</th>
                <th ngbTooltip="How the current certification was granted">Granted via</th>
                <th ngbTooltip="Grant, renew, or revoke this agent's certification">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (row of roster; track row.principal_id) {
                <tr [attr.data-testid]="'cert-row-' + row.principal_id">
                  <td>
                    <div class="agent-name">{{ row.display_name || row.email }}</div>
                    <div class="agent-email">{{ row.email }}</div>
                  </td>
                  <td [attr.data-testid]="'cert-pill-' + row.principal_id">
                    <span class="bt-badge" [class]="pillClass(row)"
                          ngbTooltip="Certified = eligible to act on cases now; Expired = was certified but the expiry has passed; Not certified = never granted">
                      {{ pillLabel(row) }}
                    </span>
                  </td>
                  <td [attr.data-testid]="'cert-expiry-' + row.principal_id">
                    {{ expiryText(row) }}
                  </td>
                  <td class="mono">{{ row.granted_via ?? '—' }}</td>
                  <td>
                    <div class="cert-actions">
                      <button type="button" class="bt-btn bt-btn--primary bt-btn--sm"
                              [attr.data-testid]="'cert-grant-' + row.principal_id"
                              [disabled]="grantingId === row.principal_id"
                              (click)="grant(row)"
                              [ngbTooltip]="row.eligibility === 'not_certified'
                                ? 'Grant this agent certification, effective now'
                                : 'Renew this agent\\'s certification, extending its expiry'">
                        {{ row.eligibility === 'not_certified' ? (grantingId === row.principal_id ? 'Granting…' : 'Grant') : (grantingId === row.principal_id ? 'Renewing…' : 'Renew') }}
                      </button>

                      @if (row.eligibility !== 'not_certified') {
                        @if (confirmingRevokeId === row.principal_id) {
                          <span class="revoke-confirm">
                            <input class="bt-input revoke-confirm__reason" type="text"
                                   [attr.data-testid]="'cert-revoke-reason-' + row.principal_id"
                                   [(ngModel)]="revokeReason" [name]="'revokeReason-' + row.principal_id"
                                   placeholder="Reason for revoking"
                                   ngbTooltip="Required — recorded on the certification's audit trail" />
                            <button type="button" class="bt-btn bt-btn--danger bt-btn--sm"
                                    [attr.data-testid]="'cert-revoke-confirm-' + row.principal_id"
                                    [disabled]="!revokeReason.trim()"
                                    (click)="confirmRevoke(row)"
                                    ngbTooltip="Confirm — this immediately blocks the agent from acting on cases">
                              Confirm revoke
                            </button>
                            <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm"
                                    [attr.data-testid]="'cert-revoke-cancel-' + row.principal_id"
                                    (click)="cancelRevoke()"
                                    ngbTooltip="Cancel — no changes will be made">
                              Cancel
                            </button>
                          </span>
                        } @else {
                          <button type="button" class="bt-btn bt-btn--danger bt-btn--sm"
                                  [attr.data-testid]="'cert-revoke-' + row.principal_id"
                                  (click)="startRevoke(row)"
                                  ngbTooltip="Revoke this agent's certification — requires a reason and blocks case access immediately">
                            Revoke
                          </button>
                        }
                      }

                      <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm"
                              [attr.data-testid]="'cert-history-' + row.principal_id"
                              (click)="toggleHistory(row)"
                              ngbTooltip="Show or hide this agent's certification exam attempt history">
                        {{ expandedId === row.principal_id ? 'Hide history' : 'History' }}
                      </button>
                    </div>
                    @if (rowError[row.principal_id]) {
                      <p class="bt-badge bt-badge--danger cert-actions__error" [attr.data-testid]="'cert-error-' + row.principal_id">
                        {{ rowError[row.principal_id] }}
                      </p>
                    }
                  </td>
                </tr>
                @if (expandedId === row.principal_id) {
                  <tr [attr.data-testid]="'attempts-row-' + row.principal_id">
                    <td colspan="5">
                      <div class="attempts-panel">
                        @if (attemptsLoading[row.principal_id]) {
                          <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading attempts…</span></div>
                        } @else if (!(attemptsById[row.principal_id]?.length)) {
                          <div class="bt-empty" [attr.data-testid]="'attempts-empty-' + row.principal_id">
                            <span class="bt-empty__hint">No exam attempts yet.</span>
                          </div>
                        } @else {
                          <table class="bt-table attempts-table" [attr.data-testid]="'attempts-table-' + row.principal_id"
                                 aria-label="Exam attempt history">
                            <thead>
                              <tr>
                                <th ngbTooltip="Attempt number, in order taken">#</th>
                                <th ngbTooltip="in_progress, submitted, or expired">Status</th>
                                <th ngbTooltip="Score achieved on this attempt">Score</th>
                                <th ngbTooltip="Whether this attempt met the passing threshold">Passed</th>
                                <th ngbTooltip="When this attempt was started">Started</th>
                                <th ngbTooltip="When this attempt was submitted or expired">Submitted</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (a of attemptsById[row.principal_id]; track a.attempt_number) {
                                <tr [attr.data-testid]="'attempt-row-' + row.principal_id + '-' + a.attempt_number">
                                  <td>{{ a.attempt_number }}</td>
                                  <td>{{ a.status }}</td>
                                  <td>{{ scoreText(a) }}</td>
                                  <td>{{ a.passed === null ? '—' : (a.passed ? 'Yes' : 'No') }}</td>
                                  <td>{{ a.started_at ? (a.started_at | date:'short') : '—' }}</td>
                                  <td>{{ a.submitted_at ? (a.submitted_at | date:'short') : '—' }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        }
                        @if (attemptsError[row.principal_id]) {
                          <p class="bt-badge bt-badge--danger" [attr.data-testid]="'attempts-error-' + row.principal_id">
                            {{ attemptsError[row.principal_id] }}
                          </p>
                        }

                        @if (confirmingResetId === row.principal_id) {
                          <span class="reset-confirm">
                            <button type="button" class="bt-btn bt-btn--danger bt-btn--sm"
                                    [attr.data-testid]="'reset-confirm-' + row.principal_id"
                                    [disabled]="!!resetting[row.principal_id]"
                                    (click)="confirmReset(row)"
                                    ngbTooltip="Confirm — this clears the attempt cap so the agent can retake the exam">
                              {{ resetting[row.principal_id] ? 'Resetting…' : 'Confirm reset' }}
                            </button>
                            <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm"
                                    [attr.data-testid]="'reset-cancel-' + row.principal_id"
                                    (click)="cancelReset()"
                                    ngbTooltip="Cancel — no changes will be made">
                              Cancel
                            </button>
                          </span>
                        } @else {
                          <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm"
                                  [attr.data-testid]="'reset-attempts-' + row.principal_id"
                                  (click)="startReset(row)"
                                  ngbTooltip="Reset this agent's exam attempts, allowing them to retake the certification exam">
                            Reset attempts
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        }
      </div>
    </div>
  `,
  styles: [`
    .cert-panel { padding: 0.5rem 1rem 1rem; }
    .agent-name { font-weight: 500; color: var(--bt-text-primary); }
    .agent-email { font-size: 12px; color: var(--bt-text-secondary); }
    .mono { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 13px; color: var(--bt-text-secondary); }
    .cert-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
    .cert-actions__error { margin: 0.4rem 0 0; white-space: normal; }
    .revoke-confirm { display: inline-flex; align-items: center; gap: 0.4rem; }
    .revoke-confirm__reason { max-width: 200px; }
    .attempts-panel { padding: 0.75rem 0.5rem; }
    .attempts-table { margin-bottom: 0.6rem; }
    .reset-confirm { display: inline-flex; align-items: center; gap: 0.4rem; }
    .bt-table th:nth-child(1), .bt-table td:nth-child(1) { width: 26%; }
    .bt-table th:nth-child(2), .bt-table td:nth-child(2) { width: 14%; }
    .bt-table th:nth-child(3), .bt-table td:nth-child(3) { width: 18%; }
    .bt-table th:nth-child(4), .bt-table td:nth-child(4) { width: 14%; }
    .bt-table th:nth-child(5), .bt-table td:nth-child(5) { width: 28%; }
  `],
})
export class AgentCertificationComponent implements OnInit {
  private api = inject(SupportApiService);
  roster: CertificationRosterEntry[] = [];
  loading = true;
  rowError: Record<string, string | null> = {};

  // Signal-backed for the same NG0100 reason as VarPrincipalReviewComponent:
  // this component's spec writes these directly on the instance outside a
  // zone-tracked template event, and only a signal write reliably notifies
  // the `@if`/`[(ngModel)]` consumers synchronously between two
  // detectChanges() calls.
  private grantingIdSignal = signal<string | null>(null);
  get grantingId(): string | null { return this.grantingIdSignal(); }
  set grantingId(value: string | null) { this.grantingIdSignal.set(value); }

  private confirmingRevokeIdSignal = signal<string | null>(null);
  get confirmingRevokeId(): string | null { return this.confirmingRevokeIdSignal(); }
  set confirmingRevokeId(value: string | null) { this.confirmingRevokeIdSignal.set(value); }

  private revokeReasonSignal = signal('');
  get revokeReason(): string { return this.revokeReasonSignal(); }
  set revokeReason(value: string) { this.revokeReasonSignal.set(value); }

  // --- Exam attempt history + reset (Task 9 admin panel, AB#1827) -----------
  // expandedId/confirmingResetId are signal-backed for the same reason as
  // grantingId/confirmingRevokeId above. attemptsById/attemptsLoading/
  // attemptsError/resetting are plain maps updated only from click handlers
  // (never written directly by a test between two detectChanges() calls), so
  // they don't need the same treatment.
  private expandedIdSignal = signal<string | null>(null);
  get expandedId(): string | null { return this.expandedIdSignal(); }
  set expandedId(value: string | null) { this.expandedIdSignal.set(value); }

  private confirmingResetIdSignal = signal<string | null>(null);
  get confirmingResetId(): string | null { return this.confirmingResetIdSignal(); }
  set confirmingResetId(value: string | null) { this.confirmingResetIdSignal.set(value); }

  attemptsById: Record<string, ExamAttemptSummary[]> = {};
  attemptsLoading: Record<string, boolean> = {};
  attemptsError: Record<string, string | null> = {};
  resetting: Record<string, boolean> = {};

  ngOnInit(): void {
    this.refresh();
  }

  private refresh(): void {
    this.loading = true;
    this.api.listCertifications().subscribe({
      next: (roster) => { this.roster = roster; this.loading = false; },
      error: () => { this.roster = []; this.loading = false; },
    });
  }

  pillLabel(row: CertificationRosterEntry): string {
    if (row.eligibility === 'certified') return 'Certified';
    if (row.eligibility === 'expired') return 'Expired';
    return 'Not certified';
  }

  pillClass(row: CertificationRosterEntry): string {
    if (row.eligibility === 'certified') return 'bt-badge--success';
    if (row.eligibility === 'expired') return 'bt-badge--danger';
    return 'bt-badge--neutral';
  }

  /** Days-to-expiry alongside the raw date — "in N days" while live, "N days
   * ago" once it has lapsed, so an operator doesn't have to do date math. */
  expiryText(row: CertificationRosterEntry): string {
    if (!row.expires_at) return '—';
    const date = new Date(row.expires_at);
    const days = Math.round((date.getTime() - Date.now()) / MS_PER_DAY);
    const dateStr = date.toLocaleDateString();
    if (days >= 0) return `${dateStr} (in ${days} day${days === 1 ? '' : 's'})`;
    return `${dateStr} (${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago)`;
  }

  grant(row: CertificationRosterEntry): void {
    if (this.grantingId === row.principal_id) return;
    this.grantingId = row.principal_id;
    this.rowError = { ...this.rowError, [row.principal_id]: null };
    this.api.grantCertification(row.principal_id).subscribe({
      next: (status) => {
        this.roster = this.roster.map((r) => (r.principal_id === row.principal_id
          ? { ...r, eligibility: status.eligibility, expires_at: status.expires_at, granted_via: status.granted_via }
          : r));
        this.grantingId = null;
      },
      error: () => {
        this.grantingId = null;
        this.rowError = { ...this.rowError, [row.principal_id]: 'Could not grant/renew certification. Try again.' };
      },
    });
  }

  startRevoke(row: CertificationRosterEntry): void {
    this.confirmingRevokeId = row.principal_id;
    this.revokeReason = '';
  }

  cancelRevoke(): void {
    this.confirmingRevokeId = null;
    this.revokeReason = '';
  }

  confirmRevoke(row: CertificationRosterEntry): void {
    const reason = this.revokeReason.trim();
    if (!reason) return;
    this.api.revokeCertification(row.principal_id, reason).subscribe({
      next: (status) => {
        this.roster = this.roster.map((r) => (r.principal_id === row.principal_id
          ? { ...r, eligibility: status.eligibility, expires_at: status.expires_at, granted_via: status.granted_via }
          : r));
        this.confirmingRevokeId = null;
        this.revokeReason = '';
      },
      error: () => {
        this.rowError = { ...this.rowError, [row.principal_id]: 'Could not revoke certification. Try again.' };
      },
    });
  }

  scoreText(attempt: ExamAttemptSummary): string {
    return attempt.score !== null ? `${Math.round(attempt.score * 100)}%` : '—';
  }

  toggleHistory(row: CertificationRosterEntry): void {
    if (this.expandedId === row.principal_id) {
      this.expandedId = null;
      return;
    }
    this.expandedId = row.principal_id;
    this.confirmingResetId = null;
    if (!this.attemptsById[row.principal_id]) {
      this.loadAttempts(row.principal_id);
    }
  }

  private loadAttempts(principalId: string): void {
    this.attemptsLoading = { ...this.attemptsLoading, [principalId]: true };
    this.attemptsError = { ...this.attemptsError, [principalId]: null };
    this.api.getExamAttempts(principalId).subscribe({
      next: (attempts) => {
        this.attemptsById = { ...this.attemptsById, [principalId]: attempts };
        this.attemptsLoading = { ...this.attemptsLoading, [principalId]: false };
      },
      error: () => {
        this.attemptsLoading = { ...this.attemptsLoading, [principalId]: false };
        this.attemptsError = { ...this.attemptsError, [principalId]: 'Could not load attempt history.' };
      },
    });
  }

  startReset(row: CertificationRosterEntry): void {
    this.confirmingResetId = row.principal_id;
  }

  cancelReset(): void {
    this.confirmingResetId = null;
  }

  confirmReset(row: CertificationRosterEntry): void {
    if (this.resetting[row.principal_id]) return;
    this.resetting = { ...this.resetting, [row.principal_id]: true };
    this.api.resetExamAttempts(row.principal_id).subscribe({
      next: () => {
        this.resetting = { ...this.resetting, [row.principal_id]: false };
        this.confirmingResetId = null;
        // Historical attempts remain (reset only lifts the attempt cap) — refresh
        // to reflect anything the server-side marker itself changed.
        this.loadAttempts(row.principal_id);
      },
      error: () => {
        this.resetting = { ...this.resetting, [row.principal_id]: false };
        this.attemptsError = { ...this.attemptsError, [row.principal_id]: 'Could not reset attempts. Try again.' };
      },
    });
  }
}
